import { api, ApiError } from '../api.js'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const invalidResponse = () => new ApiError('Invalid PTS-2 operator-tag response', 502, 'pts2_operator_tag_response_invalid')
const invalidQuery = () => new ApiError('Invalid PTS-2 operator-tag request', 400, 'pts2_operator_tag_query_invalid')

function positiveId(value) {
  value = Number(value)
  if (!Number.isSafeInteger(value) || value < 1) throw invalidQuery()
  return value
}

function projectTag(value, stationId, controllerId) {
  if (!isRecord(value) || !uuid.test(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || typeof value.name !== 'string' || [...value.name].length < 1 || [...value.name].length > 20
    || typeof value.maskedTag !== 'string' || typeof value.valid !== 'boolean'
    || !Array.isArray(value.allowedPumpIds) || value.allowedPumpIds.length > 100
    || new Set(value.allowedPumpIds).size !== value.allowedPumpIds.length
    || value.allowedPumpIds.some((id) => !Number.isSafeInteger(id) || id < 1 || id > 100)
    || !['PRESENT', 'ABSENT', 'UNKNOWN'].includes(value.remotePresence)
    || !(value.observedAt === null || typeof value.observedAt === 'string' && Number.isFinite(Date.parse(value.observedAt)))
    || !Number.isSafeInteger(value.version) || value.version < 1) throw invalidResponse()
  return { id: value.id, stationId, controllerId, name: value.name, maskedTag: value.maskedTag,
    valid: value.valid, allowedPumpIds: [...value.allowedPumpIds], remotePresence: value.remotePresence,
    observedAt: value.observedAt, version: value.version }
}

function stationQuery(stationId) {
  return new URLSearchParams({ stationId: String(positiveId(stationId)) })
}

function projectSummary(value, stationId, controllerId) {
  if (!isRecord(value) || !uuid.test(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || !['TAG_CREATE', 'TAG_UPDATE', 'TAG_DELETE'].includes(value.kind) || typeof value.state !== 'string'
    || !Number.isSafeInteger(value.actorUserId) || typeof value.actorUsername !== 'string'
    || typeof value.createdAt !== 'string' || typeof value.resolved !== 'boolean') throw invalidResponse()
  return { id: value.id, stationId, controllerId, kind: value.kind, state: value.state,
    actorUserId: value.actorUserId, actorUsername: value.actorUsername, createdAt: value.createdAt,
    confirmedAt: value.confirmedAt ?? null, completedAt: value.completedAt ?? null,
    resolved: value.resolved, targetCount: value.targetCount }
}

export async function listOperatorTags(stationId, controllerId, { search, state = 'ALL', cursor, limit = 50, signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!['ALL', 'ACTIVE', 'BLOCKED', 'UNKNOWN'].includes(state) || !Number.isSafeInteger(limit) || limit < 1 || limit > 200) throw invalidQuery()
  const query = stationQuery(stationId)
  query.set('state', state)
  query.set('limit', String(limit))
  if (search?.trim()) query.set('search', search.trim())
  if (cursor) query.set('cursor', cursor)
  const payload = await api(`/pts2/controllers/${controllerId}/operator-tags?${query}`, { method: 'GET', signal })
  if (!isRecord(payload) || !Array.isArray(payload.items) || typeof payload.registryVersion !== 'string'
    || !isRecord(payload.inventory) || !isRecord(payload.authorizationMode)
    || !['ENABLED', 'DISABLED', 'UNKNOWN'].includes(payload.authorizationMode.state)
    || typeof payload.hasMore !== 'boolean' || !(payload.nextCursor === null || typeof payload.nextCursor === 'string')) throw invalidResponse()
  return { items: payload.items.map((item) => projectTag(item, stationId, controllerId)), nextCursor: payload.nextCursor,
    hasMore: payload.hasMore, registryVersion: payload.registryVersion, inventory: structuredClone(payload.inventory),
    authorizationMode: structuredClone(payload.authorizationMode) }
}

export async function getOperatorTag(stationId, controllerId, tagId, { signal } = {}) {
  stationId = positiveId(stationId)
  controllerId = positiveId(controllerId)
  if (!uuid.test(tagId)) throw invalidQuery()
  const payload = await api(`/pts2/controllers/${controllerId}/operator-tags/${tagId}?${stationQuery(stationId)}`, { method: 'GET', signal })
  if (!isRecord(payload) || !Array.isArray(payload.operations) || payload.operations.length > 50) throw invalidResponse()
  return { tag: projectTag(payload.tag, stationId, controllerId),
    operations: payload.operations.map((item) => projectSummary(item, stationId, controllerId)) }
}

function validateDraft(draft, create) {
  if (!isRecord(draft) || typeof draft.name !== 'string' || [...draft.name].length < 1
    || [...draft.name].length > 20 || /[\p{Cc}]/u.test(draft.name)
    || typeof draft.valid !== 'boolean' || !Array.isArray(draft.allowedPumpIds)
    || new Set(draft.allowedPumpIds).size !== draft.allowedPumpIds.length
    || draft.allowedPumpIds.some((id) => !Number.isSafeInteger(id) || id < 1 || id > 100)
    || create && (typeof draft.tagValue !== 'string' || !/^[0-9A-F]{1,32}$/.test(draft.tagValue))) throw invalidQuery()
}

async function preview(stationId, controllerId, kind, expectedVersion, intent, signal) {
  if (typeof expectedVersion !== 'string' || expectedVersion.length < 1 || expectedVersion.length > 128) throw invalidQuery()
  const payload = await api(`/pts2/controllers/${positiveId(controllerId)}/operation-previews?${stationQuery(stationId)}`, {
    method: 'POST', signal, body: JSON.stringify({ kind, expectedVersion, intent }),
  })
  if (!isRecord(payload) || !uuid.test(payload.previewId) || payload.state !== 'PREVIEW'
    || typeof payload.expiresAt !== 'string' || typeof payload.canConfirm !== 'boolean') throw invalidResponse()
  return structuredClone(payload)
}

export function createOperatorTagPreview(stationId, controllerId, registryVersion, draft, { signal } = {}) {
  validateDraft(draft, true)
  return preview(stationId, controllerId, 'TAG_CREATE', registryVersion, {
    tagValue: draft.tagValue, name: draft.name, valid: draft.valid, allowedPumpIds: [...draft.allowedPumpIds],
  }, signal)
}

export function updateOperatorTagPreview(stationId, controllerId, tag, draft, { signal } = {}) {
  validateDraft(draft, false)
  if (!isRecord(tag) || !uuid.test(tag.id) || !Number.isSafeInteger(tag.version)) throw invalidQuery()
  return preview(stationId, controllerId, 'TAG_UPDATE', String(tag.version), {
    tagId: tag.id, name: draft.name, valid: draft.valid, allowedPumpIds: [...draft.allowedPumpIds],
  }, signal)
}

export function deleteOperatorTagPreview(stationId, controllerId, tag, { signal } = {}) {
  if (!isRecord(tag) || !uuid.test(tag.id) || !Number.isSafeInteger(tag.version)) throw invalidQuery()
  return preview(stationId, controllerId, 'TAG_DELETE', String(tag.version), { tagId: tag.id }, signal)
}

export async function confirmOperatorTagOperation(stationId, controllerId, previewId, idempotencyKey, { signal } = {}) {
  if (!uuid.test(previewId) || !uuid.test(idempotencyKey)) throw invalidQuery()
  const payload = await api(`/pts2/controllers/${positiveId(controllerId)}/operations?${stationQuery(stationId)}`, {
    method: 'POST', signal, body: JSON.stringify({ previewId, idempotencyKey }),
  })
  if (!isRecord(payload) || !uuid.test(payload.id) || !['TAG_CREATE', 'TAG_UPDATE', 'TAG_DELETE'].includes(payload.kind)) throw invalidResponse()
  return structuredClone(payload)
}
